import { useEffect, useState } from "react";
import FarmService from "../../../services/farmService";
import { FarmData } from "../../../interfaces/FarmData";
import keycloak from "../../../config/keycloak";
import { useSharedState } from "../../../config/FarmContext";
import UserSettingService from "../../../services/userSettingService";
import LocationMap from "./LocationMap";
import loadUserSettings from '../../../hooks/userSettingsHook';
import { Snackbar } from "../../common/Snackbar";
import { SnackbarProps } from "../../common/SnackbarProps";

interface FormProps {
    onSave: () => void;
}

const FarmFormTab = (_: FormProps) => {
    const farmService = new FarmService();
    const [isNew, setIsNew] = useState<boolean>(false);
    const userSettingService = new UserSettingService();
    const [snackbar, setSnackbar] = useState<SnackbarProps>({ active: false });
    const { farm, setFarm, userSettings, setUserSettings, farms, setFarms } = useSharedState();
    const [formData, setFormData] = useState<FarmData>({
        id: undefined,
        name: "",
        owner: keycloak.tokenParsed?.preferred_username || "",
        coordinatesX: undefined,
        coordinatesY: undefined
    });
    
    loadUserSettings();
    useEffect(() => {
        const fetchFarms = async () => {
            try {
                if (farm) {
                    setFormData(farm)
                    setFarm(farm)
                } else if (farms && farms.length > 0) {
                    setFormData(farms[0])
                    setFarm(farms[0])
                } else {
                    setIsNew(true)
                }
            } catch (error) {
                console.error("Error fetching farm data:", error);
            }
        };
        fetchFarms();
    }, [farm]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const createOrUpdateUserSettings = async (id: number) => {
        if (userSettings && userSettings.currentFarmId) {
            if (userSettings.currentFarmId === id) return;

            const settings = { ...userSettings, currentFarmId: id };
            userSettingService.updateUserSettings(settings, userSettings.id!);
            setUserSettings(settings)
        } else {
            const settings = { "username": keycloak.tokenParsed?.preferred_username, "currentFarmId": id }
            const response = await userSettingService.createUserSettings(settings)
            if (response)
                setUserSettings(response)
        }
    };

    const triggerSnackbar = (message: string, type: "success" | "error" | "info" | "warning") => {
        setSnackbar({ message: message, type: type, active: true });
        setTimeout(() => {
            setSnackbar({ active: false });
        }, 3000);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.coordinatesY || !formData.coordinatesY) {
            triggerSnackbar("Bitte setzte einen Betriebsstandort ❌", "error");
            return;
        }


        try {
            let newFarm: FarmData | null = null;
            if(isNew && !formData.id){
                 newFarm = await farmService.createFarm(formData);
            } else {
                newFarm = await farmService.updateFarm(formData.id!, formData)
            }
            
            setIsNew(false);
            setFarm(newFarm)
            createOrUpdateUserSettings(newFarm.id!);
            triggerSnackbar("Speichern erfolgreich ✅", "success")
        } catch (error: any) {
            triggerSnackbar("Betrieb konnte nicht gespeichert werden ❌", "error")
            return;
        }

        const data = await farmService.getFarms()
        setFarms(data)
    };

    const handleLocationChange = (coordinates: { lat: number; lon: number }) => {
        setFormData((prev) => ({ ...prev, coordinatesX: coordinates.lat, coordinatesY: coordinates.lon }));
    };

    const getFarmById = (id: number) => {
        return farms?.find((farm) => farm.id === id);
    }

    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedFarmId = Number(e.target.value);
        const selectedFarm = getFarmById(selectedFarmId);

        if (selectedFarm) {
            setFormData(selectedFarm);
            setIsNew(false);
            setFarm(selectedFarm)
            createOrUpdateUserSettings(selectedFarmId)
        } else {
            setIsNew(true);
            setFormData({
                name: "",
                owner: keycloak.tokenParsed?.preferred_username,
                coordinatesX: undefined,
                coordinatesY: undefined,
            });
        }
    };

    return (
        <div className="flex flex-col md:flex-row gap-2">
             <div className="flex-1 border border-gray-300 p-3 rounded-lg bg-white shadow max-h-[750px]">
                    <form className="w-full mb-6">
                        <h2 className="text-xl font-semibold py-6 pb-2">Aktueller Betrieb:</h2>
                        <select id="farm-select" className="py-1.5 px-3 w-full rounded-lg bg-gray-50 border border-gray-200" value={formData?.id || 'create'} onChange={handleSelectChange}>
                            {farms?.map((farm) => (
                                <option key={farm.id} value={farm.id}>
                                    {farm.name}
                                </option>
                            ))}
                            <option key="create" value="create">
                                Neuer Betrieb erstellen
                            </option>
                        </select>
                    </form>
                <h2 className="text-xl font-semibold py-6 pb-2">Betriebsinformationen</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label htmlFor="name" className="text-sm font-semibold text-gray-700 mb-1">Name</label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            required
                            onChange={handleChange}
                            value={formData?.name || ""}
                            className="py-1.5 px-3 w-full rounded-lg bg-gray-50 border border-gray-200"
                        />
                    </div>
                    <div>
                        <label htmlFor="owner" className="text-sm font-semibold text-gray-700 mb-1">Besitzer</label>
                        <input
                            type="text"
                            id="owner"
                            name="owner"
                            value={formData?.owner || keycloak.tokenParsed?.preferred_username}
                            onChange={handleChange}
                            required
                            disabled
                            className="py-1.5 px-3 w-full rounded-lg bg-gray-50 border border-gray-200"
                        />
                    </div>
                    <div>
                        <label htmlFor="x" className="text-sm font-semibold text-gray-700 mb-1">X Koordinaten</label>
                        <input
                            type="number"
                            id="x"
                            name="coordinatesX"
                            value={formData?.coordinatesX || ""}
                            onChange={handleChange}
                            required
                            readOnly
                            disabled
                            className="py-1.5 px-3 w-full rounded-lg bg-gray-50 border border-gray-200"
                        />
                    </div>
                    <div>
                        <label htmlFor="y" className="text-sm font-semibold text-gray-700 mb-1">Y Koordinaten</label>
                        <input
                            type="number"
                            id="y"
                            name="coordinatesY"
                            value={formData?.coordinatesY || ""}
                            onChange={handleChange}
                            required
                            readOnly
                            disabled
                            className="py-1.5 px-3 w-full rounded-lg bg-gray-50 border border-gray-200"
                        />
                    </div>
                    <div className="flex justify-center items-center py-2">
                        <button className="btn py-2.5 cursor-pointer text-base rounded-xl w-full" type="submit">Speichern</button>
                    </div>
                </form>
            </div>

             <div className="flex-1 border border-gray-300 p-3 rounded-lg bg-white shadow max-h-[750px]">
                <LocationMap onAddressSelect={handleLocationChange} position={[formData?.coordinatesX, formData?.coordinatesY]} />
            </div>

            {snackbar.active && <Snackbar message={snackbar.message!} type={snackbar.type} />}
        </div>
    );
}

export default FarmFormTab;
